// src/config/featureFlags.ts

// Flag helper reads build-time env vars (CRA uses process.env.REACT_APP_*)
const toBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return defaultValue;
};

const readRuntimeOverride = (flagName: string): boolean | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const windowFlags = (window as typeof window & { __DMS_FEATURE_FLAGS__?: Record<string, unknown> })
    .__DMS_FEATURE_FLAGS__;
  const windowOverride = windowFlags?.[flagName];
  if (typeof windowOverride === 'boolean') {
    return windowOverride;
  }

  const localStorageValue = window.localStorage.getItem(`feature:${flagName}`);
  if (localStorageValue === 'true') {
    return true;
  }
  if (localStorageValue === 'false') {
    return false;
  }

  return undefined;
};

const readFeatureFlag = (flagName: string, envValue: string | undefined, defaultValue: boolean): boolean => {
  const runtimeOverride = readRuntimeOverride(flagName);
  if (runtimeOverride !== undefined) {
    return runtimeOverride;
  }

  return toBoolean(envValue, defaultValue);
};

// Controls visibility of Map View across navigation and routing
export const isMapViewEnabled = (): boolean => {
  return readFeatureFlag('mapView', process.env.REACT_APP_ENABLE_MAP_VIEW, true);
};

export const FEATURE_FLAGS = {
  mapView: isMapViewEnabled(),
  mapViewRequiresBeta: readFeatureFlag(
    'mapViewRequiresBeta',
    process.env.REACT_APP_REQUIRE_BETA_FOR_MAP_VIEW,
    false
  ),
  welllynxLayout: readFeatureFlag('wellynxLayout', process.env.REACT_APP_USE_WELLLYNX_LAYOUT, true),
  ingestionObservability: readFeatureFlag(
    'ingestionObservability',
    process.env.REACT_APP_ENABLE_INGESTION_OBSERVABILITY,
    true
  ),
  // Build-time only. Window/localStorage cannot re-enable this. Intelift /
  // Halliburton / prod image builds must pass false (enforced in Dockerfile and
  // production pipelines); non-production test builds may pass true.
  technicalDiagnostics: toBoolean(
    process.env.REACT_APP_ENABLE_TECHNICAL_DIAGNOSTICS,
    false
  ),
  actionsTracking: readFeatureFlag(
    'actionsTracking',
    process.env.REACT_APP_ENABLE_ACTIONS_TRACKING,
    true
  ),
  rtuInfoAction: readFeatureFlag(
    'rtuInfoAction',
    process.env.REACT_APP_ENABLE_RTU_INFO_ACTION,
    true
  ),
  showNotifications: readFeatureFlag(
    'showNotifications',
    process.env.REACT_APP_ENABLE_NOTIFICATIONS,
    false
  ),
  showProfileMenuItem: readFeatureFlag(
    'showProfileMenuItem',
    process.env.REACT_APP_ENABLE_PROFILE_MENU_ITEM,
    false
  ),
  historyChart: true,
};
